<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_LOAN_PRODUCT_NIB')"
            label="Add"
            size="md"
            @click="goToLoanProduct"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="refLoanProduct"
      :items="productList"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records found"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: Default Rate -->
      <template #cell(defaultRate)="data">
        <p class="mb-0">
          {{ toNumber(data.value) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge></template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_LOAN_PRODUCT_NIB')"
              @click="goToEditLoanProduct(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Modify</span>
            </b-dropdown-item>
            <b-dropdown-item @click="goToEditLoanProduct(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_LOAN_PRODUCT_NIB' : 'ACTIVATE_LOAN_PRODUCT_NIB')"
              @click="updateLoanProductStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? 'Deactivate' : 'Activate' }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      no-close-on-backdrop
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchLoanProducts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            label="Product Name"
            placeholder="Enter product name"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Category -->
        <b-col md="6">
          <SelectField
            id="category"
            v-model="filter.category"
            label="Product Category"
            placeholder="Select product category"
            :options="productCategoryOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Category -->

        <!--Currency  -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            label="Currency"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            placeholder="Select currency"
          />
        </b-col>
        <!--Currency  -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="product-code"
            v-model="filter.code"
            label="Product Code"
            placeholder="Enter product code"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            label="Status"
            placeholder="Select"
            :options="statusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BBadge,
  BDropdown, BDropdownItem, BSpinner,
} from 'bootstrap-vue'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import toNumber from '../../../../../helpers/toNumber'
import getStatusColor from '../../../../../helpers/getStatusColor'
import { transformBoolean } from '../../../../../helpers/transformers'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { loanProductCategory } from '../../../../../helpers/data'
import Button from '../../../../components/button/Button.vue'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    BSpinner,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'name',
      isBusy: false,
      showSpinner: false,
      productList: [],
      required,
      getStatusColor,
      transformBoolean,
      modalTitle: 'Create New FD Product',
      buttonLabel: 'Save',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true },
        { key: 'category', sortable: true },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'code', sortable: true },
        { key: 'interestRate', sortable: true, label: 'Profit Rate' },
        { key: 'tenure', sortable: true },
        { key: 'principalFrequency', sortable: true },
        { key: 'interestFrequency', sortable: true, label: 'Profit Frequency' },
        { key: 'applyPenalty', sortable: true, label: 'Penalty' },
        { key: 'applyLoanFees', sortable: true, label: 'Loan Fees' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true },
        { key: 'actions' },
      ],
      toNumber,
      productCategoryOptions: loanProductCategory,
      filter: {
        name: '',
        code: '',
        type: '',
        category: '',
        currencyCode: '',
        status: '',
        sortColumn: '',
        direction: '',
      },
      currencyCodeOptions: [],
    }
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchLoanProducts()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchLoanProducts()
    },
  },
  mounted() {
    this.fetchLoanProducts()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getProducts: 'account/getProducts',
      updateProductStatus: 'account/updateStatus',
      getAllCurrencies: 'account/getAllCurrenciesAction',
    }),
    goToLoanProduct() {
      this.$router.push({
        name: 'loan-product-nib',
        params: {
          type: 'add',
          currencyOptions: this.currencyCodeOptions,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    goToEditLoanProduct(item, activity, val) {
      this.$router.push({
        name: 'loan-product-nib',
        params: {
          type: val,
          id: item.id,
          currencyOptions: this.currencyCodeOptions,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    async fetchLoanProducts() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        this.filter.type = 'Loan'
        const response = await this.getProducts({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_LOAN_PRODUCT_NIB,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productList = data.data.map(item => ({
            ...item,
            applyPenalty: transformBoolean(item.applyPenalty),
            applyLoanFees: transformBoolean(item.applyLoanFees),
          }))
          this.totalCount = data.recordCount
        } else {
          this.productList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async updateLoanProductStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_LOAN_PRODUCT_NIB : accountPermissions.ACTIVATE_LOAN_PRODUCT_NIB

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateProductStatus({
            statusValues, actionType, routeName: 'Product', permission,
          })
          const { status } = await response
          if (status === 200) {
            this.fetchLoanProducts()
            this.showSpinner = false
            this.alertSuccess({ text: `Your request to ${action} Loan Product was successful` })
          }
        }
        this.confirm(updateStatus, { title: '', text: `Are you sure you want to ${action} this Loan Product?` })
      } catch (error) {
        this.showSpinner = false
        this.alertError({ text: `Your request to ${action} Loan Product failed. Reason: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },

    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: accountPermissions.VIEW_LOAN_PRODUCT_NIB })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchLoanProducts()
    },
  },
}
</script>
